'use client';

// The admin phone app's offline mode (BL-2342, ADR 066 — Offline Mode § 5).
// Mounted once inside the realtime stack, it decides whether the app is
// online, offline by the operator's choice, or offline because there is no
// connection — and, in either offline mode, suspends the realtime transport so
// no subscription or catch-up runs until it is back online (§ 6.2).

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { useRealtimeSubscriber } from '@auctogon/core/realtime';
import { useConnectionHealth, useRecheckConnection } from '@auctogon/core/connection-health';
import { isOfflineDocumentPath } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';
import { setOfflineModeActive } from '../../api-client/offline-transport/offline-routing';
import { useIsNativeApp } from '../../hooks/useIsNativeApp';
import { useStoredPreference } from '@auctogon/ui/web';
import { MANUAL_OFFLINE_STORAGE_KEY } from './manual-offline-flag';
import { setOfflineBootNoConnection } from './offline-boot-registration';

/**
 * Whether the page is one of the precached offline documents, read synchronously for the same
 * reason as `readManualOfflineFlag` (`./manual-offline-flag`): the service worker serves one only
 * when the connection failed, so an offline cold start must open no socket or catch-up before the
 * probe answers (BL-2343, ADR 066 — Offline Mode § 6.2).
 *
 * @returns true on `/offline/…` documents; false on the server.
 */
export function readOfflineDocumentFlag(): boolean {
  return typeof window !== 'undefined' && isOfflineDocumentPath(window.location.pathname);
}

/**
 * - `online` — connected, and the operator has not chosen offline.
 * - `manual` — the operator tapped Go offline; it stays until Go online.
 * - `auto` — the reachability probe says there is no connection; it clears by itself.
 */
export type OfflineModeKind = 'online' | 'manual' | 'auto';

/** What {@link useOfflineMode} returns. */
export interface OfflineModeState {
  /** True inside the native app (or a non-production emulation of it). */
  isNativeApp: boolean;
  /** False until the platform is known; a browser-only branch waits for it. */
  nativeDetected: boolean;
  /** The current mode; always `online` outside the native app. */
  mode: OfflineModeKind;
  /** Shorthand for `mode !== 'online'`. */
  isOffline: boolean;
  /** ISO time the current offline mode began, or null when online. */
  since: string | null;
  /** Enter manual offline mode. A no-op outside the native app. */
  goOffline: () => void;
  /** Leave manual offline mode and recheck the connection. */
  goOnline: () => void;
}

const NOOP = (): void => {};

const DESKTOP: OfflineModeState = {
  isNativeApp: false,
  nativeDetected: true,
  mode: 'online',
  isOffline: false,
  since: null,
  goOffline: NOOP,
  goOnline: NOOP,
};

const OfflineModeContext = createContext<OfflineModeState>(DESKTOP);

/**
 * The time the probe first reported no connection, while it still does.
 *
 * @param probeOffline - the connection monitor's verdict.
 * @returns an ISO time, or null while connected.
 */
function useAutoOfflineSince(probeOffline: boolean): string | null {
  // Adjusted during render when the verdict flips (React's "storing
  // information from previous renders" pattern), not in an effect, so the
  // time never lags a render behind the mode it labels.
  const [seen, setSeen] = useState<{ offline: boolean; since: string | null }>({
    offline: false,
    since: null,
  });
  if (seen.offline !== probeOffline) {
    const next = { offline: probeOffline, since: probeOffline ? new Date().toISOString() : null };
    setSeen(next);
    return next.since;
  }
  return seen.since;
}

/**
 * Ask for a reachability probe when the app has no other hint: on mount (an
 * offline cold start never connected its socket, so no drop is reported, and
 * Android keeps `navigator.onLine` true in airplane mode) and on every return to
 * the foreground.
 *
 * @param enabled - only inside the native app.
 * @param recheck - the monitor's recheck.
 * @sideEffect Registers a `visibilitychange` listener; issues probes.
 */
function useNativeRechecks(enabled: boolean, recheck: () => void): void {
  useEffect(() => {
    if (!enabled) return;
    recheck();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, recheck]);
}

/**
 * Provide the offline mode to everything underneath. Must sit inside
 * `RealtimeStack` (it reads the subscriber and the connection monitor).
 *
 * @param props.children - the app.
 * @returns the provider.
 * @sideEffect Inside the native app: suspends the realtime transport while
 *   offline, probes on mount and on foreground, and writes the manual flag.
 */
export function OfflineModeProvider({ children }: { children: ReactNode }) {
  const { isNativeApp, detected: nativeDetected } = useIsNativeApp();
  const subscriber = useRealtimeSubscriber();
  const { isOffline: probeOffline } = useConnectionHealth();
  const recheck = useRecheckConnection();
  const { value: manualSince, setValue: setManualSince } = useStoredPreference(
    isNativeApp ? MANUAL_OFFLINE_STORAGE_KEY : null,
  );
  // An offline document exists only because a navigation failed, so it is offline from its first
  // render, before the probe has answered (BL-2343).
  const onOfflineDocument = isOfflineDocumentPath(usePathname() ?? '');
  const autoSince = useAutoOfflineSince(probeOffline || onOfflineDocument);

  let mode: OfflineModeKind = 'online';
  if (isNativeApp && manualSince) mode = 'manual';
  else if (isNativeApp && (probeOffline || onOfflineDocument)) mode = 'auto';

  // ADR 066 — Offline Mode § 6.2: either offline mode tears down the socket and
  // every catch-up. Nothing runs before the platform is known, because the
  // mount may have started the transport suspended from the stored manual flag
  // (`readManualOfflineFlag`). Once known, a browser resumes such a stale
  // suspension, and an unsuspended browser transport is never touched (a
  // resume of a running transport is a no-op), so RAC 1 holds.
  useEffect(() => {
    if (!nativeDetected) return;
    subscriber.setSuspended?.(isNativeApp && mode !== 'online');
  }, [nativeDetected, isNativeApp, mode, subscriber]);

  // BL-2343: the typed client answers from the device store while offline (ADR 066 § 3).
  // BUG-2103: a layout effect, because React runs a child's passive effects before its parent's.
  // As a passive effect, a screen's re-read on the way back online (`useRereadWhenBackOnline`)
  // was still answered by the device store, and a read on the way offline still reached the
  // server. Every layout effect in a commit runs before any passive effect in it.
  useLayoutEffect(() => {
    if (!nativeDetected) return;
    setOfflineModeActive(isNativeApp && mode !== 'online');
  }, [nativeDetected, isNativeApp, mode]);

  // BUG-2160: tell the offline-boot worker the app has no connection, so a screen move is answered
  // from the offline document at once instead of waiting on a network that never answers. Automatic
  // offline only: a manual offline mode may sit on a live connection. Outside the native app the
  // mark is never written (a browser has no worker).
  useEffect(() => {
    if (!nativeDetected || !isNativeApp) return;
    const caches = typeof window !== 'undefined' && 'caches' in window ? window.caches : undefined;
    setOfflineBootNoConnection({ caches, noConnection: mode === 'auto' }).catch((err: unknown) => {
      // A missing mark only costs the old network wait. Logged, not surfaced: nothing to show.
      // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Capacitor forwards it to Xcode / logcat.
      console.warn('[offline-boot] could not update the no-connection mark', err instanceof Error ? err : new Error(String(err)));
    });
  }, [nativeDetected, isNativeApp, mode]);

  useNativeRechecks(isNativeApp, recheck);

  const goOffline = useCallback(() => {
    if (!isNativeApp) return;
    setManualSince(new Date().toISOString());
  }, [isNativeApp, setManualSince]);

  const goOnline = useCallback(() => {
    setManualSince(null);
    // The network may have gone while the app sat in manual offline mode with
    // its socket closed, leaving the monitor no hint. Ask.
    recheck();
  }, [setManualSince, recheck]);

  const value = useMemo<OfflineModeState>(
    () => ({
      isNativeApp,
      nativeDetected,
      mode,
      isOffline: mode !== 'online',
      since: mode === 'manual' ? (manualSince ?? null) : mode === 'auto' ? autoSince : null,
      goOffline,
      goOnline,
    }),
    [isNativeApp, nativeDetected, mode, manualSince, autoSince, goOffline, goOnline],
  );

  return <OfflineModeContext.Provider value={value}>{children}</OfflineModeContext.Provider>;
}

/**
 * Read the offline mode.
 *
 * @returns the state; outside a provider, always online with no-op actions.
 */
export function useOfflineMode(): OfflineModeState {
  return useContext(OfflineModeContext);
}
